/**
 * Hobby Agent - AI-Powered Hobby Recommendations
 * 
 * This agent handles:
 * - Hobby suggestions based on user profile
 * - Context-aware hobby recommendations
 * - Popular hobbies in user's location
 * - Hobby discovery based on browsing patterns
 */

class HobbyAgent {
  constructor() {
    this.userProfile = null;
    this.browsingHistory = [];
    this.locationContext = null;
  }

  /**
   * Set user profile for personalized recommendations
   */
  setUserProfile(profile) {
    this.userProfile = profile;
  }

  /**
   * Set location context for location-based recommendations
   */
  setLocationContext(location) {
    this.locationContext = location;
  }

  /**
   * Track browsing history for pattern analysis
   */
  trackBrowsing(group) {
    if (group && group.hobbies) {
      this.browsingHistory.push({
        hobbies: group.hobbies,
        tags: group.tags,
        timestamp: Date.now()
      });
    }
  }

  /**
   * Get hobbies from user profile
   */
  getProfileHobbies() {
    return this.userProfile?.hobbies || [];
  }

  /**
   * Suggest hobbies based on user's majors
   */
  suggestFromMajors(majors) {
    if (!majors || !Array.isArray(majors)) return [];

    const majorToHobbies = {
      'computer science': ['Programming', 'Gaming', 'Technology'],
      'engineering': ['Robotics', 'DIY Projects', 'Technology'],
      'business': ['Entrepreneurship', 'Networking', 'Finance'],
      'art': ['Art', 'Photography', 'Design'],
      'music': ['Music', 'Singing', 'Instruments'],
      'sports': ['Sports', 'Fitness', 'Running'],
      'science': ['Science', 'Research', 'Experiments'],
      'literature': ['Reading', 'Writing', 'Book Club'],
      'psychology': ['Mental Health', 'Meditation', 'Wellness'],
      'education': ['Teaching', 'Tutoring', 'Mentoring']
    };

    const suggestions = [];
    majors.forEach(major => {
      const majorLower = major.toLowerCase();
      for (const [key, hobbies] of Object.entries(majorToHobbies)) {
        if (majorLower.includes(key)) {
          suggestions.push(...hobbies);
        }
      }
    });

    return [...new Set(suggestions)]; // Remove duplicates
  }

  /**
   * Suggest hobbies based on browsing history
   */
  suggestFromHistory() {
    if (this.browsingHistory.length === 0) return [];

    // Analyze most viewed hobbies
    const hobbyFrequency = {};
    this.browsingHistory.forEach(entry => {
      entry.hobbies?.forEach(hobby => {
        hobbyFrequency[hobby] = (hobbyFrequency[hobby] || 0) + 1;
      });
    });

    // Return top 3 most frequent hobbies
    return Object.entries(hobbyFrequency)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([hobby]) => hobby);
  }

  /**
   * Get popular hobbies for a location (would call API in production)
   */
  async getPopularHobbiesForLocation(location) {
    // In production, this would call an API
    // For now, return common hobbies
    return ['Sports', 'Music', 'Art', 'Reading', 'Hiking', 'Photography'];
  }

  /**
   * Get intelligent hobby recommendations
   */
  async getIntelligentHobbies(options = {}) {
    const {
      maxSuggestions = 5,
      includeProfile = true,
      includeMajors = true,
      includeHistory = true,
      includePopular = false
    } = options;

    const suggestions = new Set();

    // 1. Add user profile hobbies
    if (includeProfile) {
      const profileHobbies = this.getProfileHobbies();
      profileHobbies.forEach(h => suggestions.add(h));
    }

    // 2. Add hobbies from majors
    if (includeMajors && this.userProfile?.majors) {
      const majorHobbies = this.suggestFromMajors(this.userProfile.majors);
      majorHobbies.forEach(h => suggestions.add(h));
    }

    // 3. Add hobbies from browsing history
    if (includeHistory) {
      const historyHobbies = this.suggestFromHistory();
      historyHobbies.forEach(h => suggestions.add(h));
    }

    // 4. Add popular hobbies for location
    if (includePopular && this.locationContext) {
      const popularHobbies = await this.getPopularHobbiesForLocation(this.locationContext);
      popularHobbies.forEach(h => suggestions.add(h));
    }

    return Array.from(suggestions).slice(0, maxSuggestions);
  }

  /**
   * Rank hobbies by relevance
   */
  rankHobbies(hobbies, context = {}) {
    return hobbies.map(hobby => ({
      hobby,
      score: this.calculateRelevanceScore(hobby, context)
    })).sort((a, b) => b.score - a.score);
  }

  /**
   * Calculate relevance score for a hobby
   */
  calculateRelevanceScore(hobby, context) {
    let score = 0;
    const hobbyLower = hobby.toLowerCase();

    // Check if in user profile
    if (this.userProfile?.hobbies?.some(h => h.toLowerCase() === hobbyLower)) {
      score += 10;
    }

    // Check if matches majors
    if (this.userProfile?.majors) {
      const majorMatch = this.userProfile.majors.some(major => 
        hobbyLower.includes(major.toLowerCase()) || 
        major.toLowerCase().includes(hobbyLower)
      );
      if (majorMatch) score += 5;
    }

    // Check browsing history frequency
    const historyCount = this.browsingHistory.filter(entry =>
      entry.hobbies?.some(h => h.toLowerCase() === hobbyLower)
    ).length;
    score += historyCount * 2;

    return score;
  }
}

export default HobbyAgent;

